import type { WidgetProps } from '@rjsf/utils';
import { isArray } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../../utils/theme';
import { getFileAsDataUrlWithName } from '../../../utils/get-file-as-data-url-with-name';
import FileUploaderAndList from '../../file-uploader-and-list/file-uploader-and-list';
import type { IFileUploader } from '../../file-uploader/file-uploader';
import InputLabel from '../../input-label/input-label';
import Typography from '../../typography/typography';
import { extractFileNameFromDataUrl, parseExistingFile } from '../utils';

interface FileUploaderWidgetProps extends Omit<WidgetProps, 'value'> {
  // Support multiple files if value is an array
  value: string | string[] | undefined;
}

export const ALL_FILE_EXTENSIONS = [
  'pdf',
  'txt',
  'csv',
  'doc',
  'docx',
  'xlsx',
  'xls',
  'png',
  'jpg',
  'jpeg',
  'heic',
  'webp',
];

const FileUploaderWidget = ({
  value,
  label,
  onChange,
  disabled,
  rawErrors,
  hideError,
  uiSchema,
  registry,
  schema,
}: FileUploaderWidgetProps) => {
  const formContext = registry?.formContext;
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const hasError = Boolean(rawErrors && rawErrors.length > 0 && !hideError);
  const maxFilesNumber = isArray(value) ? undefined : 1;

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      const filesAsDataUrls = await Promise.all(
        files.map((file) => getFileAsDataUrlWithName(file))
      );
      if (isArray(value)) {
        onChange([...(value ?? []), ...filesAsDataUrls]);
      } else {
        onChange(filesAsDataUrls[0]);
      }
      setIsUploading(false);
    },
    [onChange, value]
  );

  const files = useMemo(() => {
    const array = [value || []].flat(); // Convert undefined/string/array to an array
    return array.map((file: string, index: number) => {
      if (file.startsWith('data:text/url')) {
        return parseExistingFile(file);
      }
      return {
        file,
        uuid: String(index),
        originalName: extractFileNameFromDataUrl(file, index),
      };
    });
  }, [value]);

  const handleRemove = useCallback(
    (index: string) => {
      if (isArray(value)) {
        const indexAsNumber = Number(index);
        onChange([
          ...value.slice(0, indexAsNumber),
          ...value.slice(indexAsNumber + 1),
        ]);
      } else {
        onChange(undefined);
      }
    },
    [onChange, value]
  );

  const fileUploaderProps: IFileUploader = {
    uploading: isUploading,
    onFileUpload: handleFileUpload,
    acceptedFileExtensions:
      uiSchema?.acceptFileTypes ??
      formContext?.acceptFileTypes ??
      ALL_FILE_EXTENSIONS,
    uploadError: hasError ? rawErrors?.join(', ') : undefined,
    disabled,
    maxFilesNumber,
  };

  return (
    <Wrapper>
      <InputLabel theme={theme} $hasError={hasError}>
        {label}
      </InputLabel>
      {schema?.description && (
        <Typography
          variant={TypographyVariant.bodySm1}
          color={theme.palette.neutral[90]}
        >
          {schema?.description}
        </Typography>
      )}
      <InputWrapper>
        <FileUploaderAndList
          fileUploaderProps={fileUploaderProps}
          files={files}
          onRemove={handleRemove}
          enableImagePreview={true}
          imagePreviewWidth={uiSchema?.imagePreviewWidth}
          imagePreviewHeight={uiSchema?.imagePreviewHeight}
        />
      </InputWrapper>
    </Wrapper>
  );
};

export default FileUploaderWidget;

export const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

export const InputWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;
